import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeadListQuery } from 'api';
import { Form, TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { Lead, LeadFilterParams } from 'models/Lead';
import { OrderType, PaginationType, SortParamsType } from 'typings';

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useLeads = (forDashboard?: boolean) => {
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<LeadFilterParams | object>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sorter, setSorter] = useState<SortParamsType>(forDashboard ? { field: 'status', order: 'asc' } : initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { data: { data: { content = [], totalElements = 0 } = {} } = {}, isLoading } = useLeadListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: forDashboard && !sorter.field ? 'status' : sorter.field,
    sortOrder: forDashboard && !sorter.field ? 'asc' : sorter.order,
    search,
    filters,
    forDashboard,
  });

  const openDrawer = useCallback(() => setFilterDrawerVisible(true), []);
  const closeDrawer = useCallback(() => setFilterDrawerVisible(false), []);

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: LeadFilterParams) => {
      const _filters: any = {
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.clientId && { clientId: values.clientId }),
        ...(values.createdBy && { createdBy: values.createdBy }),
        ...(values.lastUpdatedBy && { lastUpdatedBy: values.lastUpdatedBy }),
        ...(values.clientId && { clientId: values.clientId }),
        ...(values.status?.length && { status: values.status }),
        ...(values.region?.length && { regionIds: values.region }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      closeDrawer();
    },
    [closeDrawer]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  const routeChange = useCallback(
    (id: string) => {
      navigate(`${ADMIN_ROUTES.leads.view}/${id}/${ADMIN_ROUTES.leads.general}`);
    },
    [navigate]
  );

  const handleTableChange: TableProps<Lead>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<Lead> | SorterResult<Lead>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSorter({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  return {
    openDrawer,
    closeDrawer,
    filterDrawerVisible,
    form,
    routeChange,
    data: content || [],
    totalElements: totalElements || 0,
    pagination,
    isLoading,
    onSearch,
    handleTableChange,
    applyFilters,
    resetFilters,
  };
};
